import { Suspense, lazy, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { RewardBadge } from '../../dashboard/components/RewardBadge';
import { useGame } from '../../game/store';
import type { EconomyNaming, WheelId } from '@shared';
import {
  creditWord,
  formatCountdown,
  formatCreditAmount,
  formatCredits,
  formatRelative,
} from '../../lib/format';
import { sfx } from '../../lib/audio';
import { readMotionOverride, writeMotionOverride } from '../../lib/motion';
import { useSession } from '../../state/session';
import { Badge } from '../ui/Badge';
import { Icon, type IconName } from '../ui/Icon';
import { creditIconFor } from '../ui/currency';
import { Panel } from '../ui/Panel';
import { Mascot } from '../mascot/Mascot';
import { WheelStage, type WheelStageHandle } from '../wheel/WheelStage';
import { hubIconFor } from '../wheel/emblem';
import { buildSlices } from '../wheel/geometry';
import type { RevealedOutcome } from '../wheel/types';
import styles from './PlayableWheel.module.css';

/**
 * The reward dialog owns Framer Motion and the confetti engine — together the
 * heaviest thing on the page, and useless until a spin lands. It is split out
 * and warmed the moment a spin starts, so the chunk is always in cache by the
 * time the wheel stops five seconds later.
 */
const loadRewardDialog = () => import('../wheel/RewardDialog');
const RewardDialog = lazy(async () => ({ default: (await loadRewardDialog()).RewardDialog }));

/** Beat between the wheel settling and the reward dialog — pure suspense. */
const REVEAL_DELAY_MS = 380;

function announce(outcome: RevealedOutcome, naming: EconomyNaming): string {
  if (outcome.reward === 'nothing') return 'No prize this time.';
  if (outcome.reward === 'credits') {
    return `You won ${formatCreditAmount(naming, outcome.value)}.`;
  }
  if (outcome.reward === 'xp') return `You won ${outcome.value} ${naming.xpName}.`;
  if (outcome.reward === 'coupon') return `You won a ${outcome.value}% off coupon.`;
  if (outcome.reward === 'respin') return 'Free respin — that one was on the house.';
  return `You won ${outcome.name}.`;
}

interface PlayableWheelProps {
  wheelId: WheelId;
}

/**
 * The player-facing spin experience, shared by the main and daily wheels.
 *
 * This component owns the *rules* — gating, when the reward may be shown, when
 * the result becomes public. The spectacle lives in `WheelStage` and
 * `RewardDialog`. Keeping the split means the economy can change without
 * touching the animation, and the animation can be reworked without risking a
 * payout bug.
 *
 * It reads the wheel through the game store. The draw's odds are not in scope
 * here — they never left the server, and the public type doesn't carry them;
 * the player sees the prizes, never a percentage.
 */
export function PlayableWheel({ wheelId }: PlayableWheelProps) {
  const { wheel, balance, spin, commitSpin, wheelStatus, spins, naming } = useGame();
  const { theme } = useSession();
  // Always animate by default — the spin is the whole point — but the header
  // toggle still lets a player turn it off. We deliberately do NOT follow the OS
  // "reduce motion" setting as the default: it was silently killing the spin for
  // players (and models) who had it on, often without realising.
  const [motionOverride, setMotionOverride] = useState<boolean | null>(() => readMotionOverride());
  const animate = motionOverride ?? true;
  const reducedMotion = !animate;
  // The hub wears the creator's emblem, except on the premium wheel, which keeps
  // its crown so the two wheels stay tellable apart under every theme.
  const hubIcon = hubIconFor(theme, wheelId);

  const config = wheel(wheelId);
  const status = wheelStatus(wheelId);

  const stage = useRef<WheelStageHandle>(null);
  const revealTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** A spin is in flight to the server. See the guard in `handleSpin`. */
  const sending = useRef(false);

  const [spinning, setSpinning] = useState(false);
  const [revealed, setRevealed] = useState<RevealedOutcome | null>(null);
  const [liveMessage, setLiveMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(() => sfx.isMuted());
  const [now, setNow] = useState(() => Date.now());

  const slices = useMemo(() => buildSlices(config.segments), [config.segments]);

  useEffect(() => {
    sfx.preload('tick', 'launch', 'win');
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(timer);
      if (revealTimer.current) clearTimeout(revealTimer.current);
      stage.current?.cancel();
      // Leaving mid-spin still pays out. The player has already been charged in
      // every sense that matters to them — the wheel was turning.
      commitSpin();
    };
  }, [commitSpin]);

  const mySpins = useMemo(() => spins.filter((s) => s.wheelId === wheelId), [spins, wheelId]);

  // No snapshotting needed: `spin` holds the server's new view back, so
  // everything read from the store here — and anywhere else in the app — is
  // still the pre-spin truth until `commitSpin` runs at the reveal.
  const remaining = status.nextAt ? new Date(status.nextAt).getTime() - now : 0;
  const canSpin = status.canSpin && !spinning && slices.length > 0;

  function toggleMute() {
    const next = !muted;
    sfx.setMuted(next);
    setMuted(next);
    if (!next) sfx.play('click');
  }

  function toggleAnimation() {
    const next = !animate;
    setMotionOverride(next);
    writeMotionOverride(next);
    if (next) sfx.play('click');
  }

  async function handleSpin() {
    // `canSpin` can't cover the round trip on its own: nothing in the store
    // changes until the server answers, so a second click during that window
    // would send a second spin and charge for it.
    if (!canSpin || sending.current) return;
    sending.current = true;
    setError(null);

    void loadRewardDialog();
    // The draw happens on the server, so this is a round trip. The reward
    // dialog's chunk is already warming while it is in flight.
    const outcome = await spin(wheelId);
    sending.current = false;

    if (!outcome.ok) {
      setError(
        outcome.reason === 'insufficient'
          ? `Not enough ${creditWord(naming)} for a spin.`
          : outcome.reason === 'cooldown'
            ? 'This wheel is on cooldown.'
            : outcome.reason === 'disabled'
              ? 'This wheel is turned off right now.'
              : 'The wheel is not ready right now.',
      );
      return;
    }

    const slice = slices.find((s) => s.segment.id === outcome.segmentId);
    setRevealed(null);
    setSpinning(true);
    setLiveMessage('Spinning the wheel…');

    await stage.current?.spinTo(slice?.mid ?? 0);

    revealTimer.current = setTimeout(
      () => {
        // Publish and reveal together — React batches these, so the balance
        // updates on the same paint as the dialog announcing why.
        commitSpin();
        setRevealed(outcome);
        setLiveMessage(announce(outcome, naming));
        setSpinning(false);
      },
      reducedMotion ? 0 : REVEAL_DELAY_MS,
    );
  }

  const costLabel = status.cost === 0 ? 'Free' : formatCredits(status.cost);
  const ctaLabel = spinning
    ? 'Spinning…'
    : status.cost === 0
      ? 'Spin — free'
      : `Spin · ${formatCreditAmount(naming, status.cost)}`;

  return (
    <>
      <div className={styles.layout}>
        <section className={styles.stageCard}>
          <header className={styles.header}>
            <span className={styles.balanceChip}>
              <Icon name={creditIconFor(theme)} size={14} />
              {formatCredits(balance)}
            </span>

            <div className={styles.headerRight}>
              <Badge tone={status.cost === 0 ? 'success' : 'brand'} dot>
                {status.cost === 0 ? 'Free spin' : `${formatCredits(status.cost)} / spin`}
              </Badge>
              {status.cooldownHours > 0 && (
                <Badge tone="neutral">1 per {status.cooldownHours}h</Badge>
              )}
              <button
                type="button"
                className={styles.soundToggle}
                onClick={toggleAnimation}
                aria-pressed={!animate}
                aria-label={animate ? 'Turn off wheel animation' : 'Turn on wheel animation'}
                title={animate ? 'Animation on' : 'Animation off'}
              >
                <Icon name={animate ? 'sparkles' : 'ban'} size={15} />
              </button>
              <button
                type="button"
                className={styles.soundToggle}
                onClick={toggleMute}
                aria-pressed={muted}
                aria-label={muted ? 'Unmute sound effects' : 'Mute sound effects'}
                title={muted ? 'Unmute' : 'Mute'}
              >
                <Icon name={muted ? 'ban' : 'bolt'} size={15} />
              </button>
            </div>
          </header>

          {/* The wheel gets its own slot so a short frame can move it beside the
              card's chrome instead of under it — see the layout stylesheet. */}
          <div className={styles.stageSlot}>
            {slices.length === 0 ? (
              <div className={styles.placeholder}>
                <Icon name="wheel" size={30} />
                <p>This wheel is being set up. Check back soon.</p>
              </div>
            ) : (
              <WheelStage
                ref={stage}
                slices={slices}
                hubIcon={hubIcon}
                hubLabel={costLabel}
                naming={naming}
                spinning={spinning}
                disabled={!canSpin}
                onSpin={handleSpin}
                reducedMotion={reducedMotion}
              />
            )}
          </div>

          <div className={styles.controls}>
            <p className={styles.tagline}>
              {spinning ? 'Good luck…' : config.description || 'Spin to try your luck'}
            </p>

            {status.onCooldown && !spinning ? (
              <div className={styles.cooldown}>
                <span className={styles.cooldownLabel}>Next spin in</span>
                <span className={styles.cooldownTime}>{formatCountdown(remaining)}</span>
              </div>
            ) : (
              <button
                type="button"
                className={styles.cta}
                onClick={handleSpin}
                disabled={!canSpin}
                aria-busy={spinning}
              >
                <Icon name="wheel" size={18} />
                {/* Kept to one line: the stage's height is `100dvh` minus a
                    constant, and a CTA that wraps eats into the wheel. */}
                <span className={styles.ctaLabel}>{ctaLabel}</span>
              </button>
            )}

            {!config.enabled ? (
              <p className={styles.hint}>This wheel is turned off right now. Check back soon.</p>
            ) : status.onCooldown ? (
              <p className={styles.hint}>
                You&apos;ve used this wheel — come back when the timer ends.
              </p>
            ) : !status.affordable ? (
              <p className={styles.hintWarn}>
                You need {formatCreditAmount(naming, status.cost - balance)} more to spin.
              </p>
            ) : error ? (
              <p className={styles.hintWarn}>{error}</p>
            ) : null}
          </div>

          <span className={styles.srOnly} role="status" aria-live="polite">
            {liveMessage}
          </span>
        </section>

        <aside className={styles.side}>
          {/* Beside the wheel, never over it: the side column has the room, and the
              wheel card's height — which the stage is sized against — stays as it
              was. The payoff reaction is the reward dialog's. */}
          <div className={styles.mascotCard}>
            <Mascot state={spinning ? 'spinning' : 'idle'} size={200} />
            <div className={styles.mascotWords}>
              <strong>{spinning ? 'Good luck…' : 'Ready when you are'}</strong>
              <span>{spinning ? 'Here it comes!' : `${config.name || 'Spin'} is waiting`}</span>
            </div>
          </div>

          <Panel title="Rewards" subtitle={`${slices.length} on this wheel`}>
            <ul className={styles.rewards}>
              {slices.map((slice) => (
                <li key={slice.segment.id} className={styles.reward}>
                  <span
                    className={styles.rewardIcon}
                    style={{ '--reward-tint': slice.segment.color } as CSSProperties}
                  >
                    <Icon name={slice.segment.icon as IconName} size={14} />
                  </span>
                  <span className={styles.rewardName}>{slice.segment.name}</span>
                </li>
              ))}
            </ul>
          </Panel>

          <Panel
            title="Your spins"
            subtitle={`${mySpins.length} spin${mySpins.length === 1 ? '' : 's'} so far`}
          >
            {mySpins.length === 0 ? (
              <p className={styles.empty}>No spins yet. Give the wheel a try!</p>
            ) : (
              <div className={styles.history}>
                {mySpins.slice(0, 6).map((entry) => (
                  <div key={entry.id} className={styles.historyItem}>
                    <span className={styles.historyDot} style={{ background: entry.color }} />
                    <span className={styles.historyBody}>
                      <span className={styles.historyLabel}>{entry.name}</span>
                      <span className={styles.historyMeta}>
                        {formatRelative(entry.at)}
                        {entry.cost > 0 ? ` · −${formatCreditAmount(naming, entry.cost)}` : ''}
                      </span>
                    </span>
                    <RewardBadge reward={entry.reward} naming={naming} />
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </aside>
      </div>

      <Suspense fallback={null}>
        <RewardDialog
          outcome={revealed}
          naming={naming}
          onClose={() => setRevealed(null)}
          reducedMotion={reducedMotion}
        />
      </Suspense>
    </>
  );
}
